export const MAX_DOCUMENT_ATTACHMENTS = 4;
export const MAX_DOCUMENT_TOTAL_BYTES = 10 * 1024 * 1024;
// Lifecycle validation must retain every accepted Slack attachment. Turn ingress
// still enforces its own smaller browser or larger Slack-specific limits.
export const MAX_STORED_DOCUMENT_BYTES = 20 * 1024 * 1024;
export const MAX_DOCUMENT_NAME_LENGTH = 160;
export const DOCUMENT_ATTACHMENT_STATE_METADATA_KEY = 'researchAttachmentState';
export const DOCUMENT_ATTACHMENT_SESSION_METADATA_KEY = 'researchSessionId';
export const DOCUMENT_ATTACHMENT_MESSAGE_METADATA_KEY = 'researchMessageId';
export const DOCUMENT_ATTACHMENT_PROJECT_METADATA_KEY = 'researchProjectId';
const MAX_ESTIMATED_BINARY_DOCUMENT_TOKENS = 200_000;

export type DocumentAttachmentKind = 'pdf' | 'text' | 'zip-office' | 'ole-office' | 'rtf';

export interface DocumentFileType {
  extension: string;
  mimeType: string;
  kind: DocumentAttachmentKind;
}

export interface DocumentAttachment {
  id: string;
  name: string;
  extension: string;
  mimeType: string;
  sizeBytes: number;
  sha256: string;
  projectId?: string;
  workspaceId?: string;
}

const DOCUMENT_FILE_TYPES: DocumentFileType[] = [
  { extension: 'pdf', mimeType: 'application/pdf', kind: 'pdf' },
  { extension: 'txt', mimeType: 'text/plain', kind: 'text' },
  { extension: 'text', mimeType: 'text/plain', kind: 'text' },
  { extension: 'md', mimeType: 'text/markdown', kind: 'text' },
  { extension: 'markdown', mimeType: 'text/markdown', kind: 'text' },
  { extension: 'csv', mimeType: 'text/csv', kind: 'text' },
  { extension: 'tsv', mimeType: 'text/tab-separated-values', kind: 'text' },
  { extension: 'json', mimeType: 'application/json', kind: 'text' },
  { extension: 'yaml', mimeType: 'application/yaml', kind: 'text' },
  { extension: 'yml', mimeType: 'application/yaml', kind: 'text' },
  { extension: 'xml', mimeType: 'application/xml', kind: 'text' },
  { extension: 'html', mimeType: 'text/html', kind: 'text' },
  { extension: 'htm', mimeType: 'text/html', kind: 'text' },
  {
    extension: 'docx',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    kind: 'zip-office',
  },
  { extension: 'doc', mimeType: 'application/msword', kind: 'ole-office' },
  {
    extension: 'odt',
    mimeType: 'application/vnd.oasis.opendocument.text',
    kind: 'zip-office',
  },
  { extension: 'rtf', mimeType: 'application/rtf', kind: 'rtf' },
  {
    extension: 'xlsx',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    kind: 'zip-office',
  },
  { extension: 'xls', mimeType: 'application/vnd.ms-excel', kind: 'ole-office' },
  {
    extension: 'pptx',
    mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    kind: 'zip-office',
  },
  { extension: 'ppt', mimeType: 'application/vnd.ms-powerpoint', kind: 'ole-office' },
];

const FILE_TYPE_BY_EXTENSION = new Map(
  DOCUMENT_FILE_TYPES.map(fileType => [fileType.extension, fileType])
);
const DOCUMENT_ID_PATTERN =
  /^doc-[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA256_PATTERN = /^[0-9a-f]{64}$/i;
const PDF_SIGNATURE = [0x25, 0x50, 0x44, 0x46, 0x2d];
const ZIP_SIGNATURES = [
  [0x50, 0x4b, 0x03, 0x04],
  [0x50, 0x4b, 0x05, 0x06],
  [0x50, 0x4b, 0x07, 0x08],
];
const OLE_SIGNATURE = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

export const DOCUMENT_ACCEPT_ATTRIBUTE = DOCUMENT_FILE_TYPES.map(
  fileType => `.${fileType.extension}`
).join(',');

export function documentFileTypeFromName(name: string): DocumentFileType | null {
  const normalized = name.trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  if (dotIndex < 0 || dotIndex === normalized.length - 1) return null;
  return FILE_TYPE_BY_EXTENSION.get(normalized.slice(dotIndex + 1)) || null;
}

export function isTextDocumentMimeType(mimeType: string): boolean {
  return DOCUMENT_FILE_TYPES.some(
    fileType => fileType.kind === 'text' && fileType.mimeType === mimeType
  );
}

export function sanitizeDocumentName(name: string): string {
  const normalized = name
    .normalize('NFC')
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[\\/]/g, '-')
    .trim();
  const fallback = normalized || 'document';
  if (fallback.length <= MAX_DOCUMENT_NAME_LENGTH) return fallback;

  const fileType = documentFileTypeFromName(fallback);
  if (!fileType) return fallback.slice(0, MAX_DOCUMENT_NAME_LENGTH);
  const suffix = `.${fileType.extension}`;
  return `${fallback.slice(0, MAX_DOCUMENT_NAME_LENGTH - suffix.length)}${suffix}`;
}

export function documentAttachmentStoragePath(
  userId: string,
  attachment: Pick<DocumentAttachment, 'id' | 'extension'>
): string {
  if (!userId || userId.includes('/') || userId.includes('..')) {
    throw new Error('Invalid document owner');
  }
  if (!DOCUMENT_ID_PATTERN.test(attachment.id)) {
    throw new Error('Invalid document attachment ID');
  }
  const fileType = FILE_TYPE_BY_EXTENSION.get(attachment.extension.toLowerCase());
  if (!fileType) {
    throw new Error('Unsupported document extension');
  }
  return `research-attachments/${userId}/${attachment.id}.${fileType.extension}`;
}

export function isDocumentAttachment(value: unknown): value is DocumentAttachment {
  return isDocumentAttachmentWithinLimit(value, MAX_DOCUMENT_TOTAL_BYTES);
}

export function isDocumentAttachmentWithinLimit(
  value: unknown,
  maxBytes = MAX_DOCUMENT_TOTAL_BYTES
): value is DocumentAttachment {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const attachment = value as Partial<DocumentAttachment>;
  if (
    typeof attachment.id !== 'string' ||
    !DOCUMENT_ID_PATTERN.test(attachment.id) ||
    typeof attachment.name !== 'string' ||
    !attachment.name.trim() ||
    attachment.name.length > MAX_DOCUMENT_NAME_LENGTH ||
    typeof attachment.extension !== 'string' ||
    typeof attachment.mimeType !== 'string' ||
    typeof attachment.sizeBytes !== 'number' ||
    !Number.isSafeInteger(attachment.sizeBytes) ||
    attachment.sizeBytes <= 0 ||
    attachment.sizeBytes > maxBytes ||
    typeof attachment.sha256 !== 'string' ||
    !SHA256_PATTERN.test(attachment.sha256) ||
    (attachment.projectId !== undefined &&
      (typeof attachment.projectId !== 'string' ||
        !attachment.projectId.trim() ||
        attachment.projectId.length > 256 ||
        /[\u0000-\u001f/\\]/.test(attachment.projectId))) ||
    (attachment.workspaceId !== undefined &&
      (typeof attachment.workspaceId !== 'string' ||
        !attachment.workspaceId.trim() ||
        attachment.workspaceId.length > 256 ||
        /[\u0000-\u001f/\\]/.test(attachment.workspaceId))) ||
    (attachment.projectId !== undefined && attachment.workspaceId !== undefined)
  ) {
    return false;
  }

  const fileType = FILE_TYPE_BY_EXTENSION.get(attachment.extension.toLowerCase());
  return Boolean(
    fileType &&
      fileType.mimeType === attachment.mimeType.toLowerCase() &&
      documentFileTypeFromName(attachment.name)?.extension === fileType.extension
  );
}

function startsWithBytes(bytes: Uint8Array, signature: number[]): boolean {
  return signature.every((value, index) => bytes[index] === value);
}

export function validateDocumentBytes(
  bytes: Uint8Array,
  fileType: DocumentFileType
): string | null {
  if (bytes.byteLength === 0) return 'The document is empty.';

  if (fileType.kind === 'pdf' && !startsWithBytes(bytes, PDF_SIGNATURE)) {
    return 'The selected file is not a valid PDF.';
  }
  if (
    fileType.kind === 'zip-office' &&
    !ZIP_SIGNATURES.some(signature => startsWithBytes(bytes, signature))
  ) {
    return 'The selected Office document is not a valid ZIP-based file.';
  }
  if (fileType.kind === 'ole-office' && !startsWithBytes(bytes, OLE_SIGNATURE)) {
    return 'The selected legacy Office document is not valid.';
  }
  if (fileType.kind === 'rtf') {
    const prefix = new TextDecoder().decode(bytes.slice(0, 32)).replace(/^\uFEFF?\s*/, '');
    if (!prefix.startsWith('{\\rtf')) return 'The selected file is not valid RTF.';
  }
  if (fileType.kind === 'text') {
    try {
      new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
      return 'Text documents must use UTF-8 encoding.';
    }
  }
  return null;
}

export function formatDocumentSize(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${Math.ceil(sizeBytes / 1024)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function estimateBinaryDocumentTokens(sizeBytes: number): number {
  return Math.min(MAX_ESTIMATED_BINARY_DOCUMENT_TOKENS, Math.max(4_000, Math.ceil(sizeBytes / 4)));
}

export function estimateDocumentAttachmentTokens(
  attachment: Pick<DocumentAttachment, 'name' | 'sizeBytes'>
): number {
  return documentFileTypeFromName(attachment.name)?.kind === 'text'
    ? Math.max(1, attachment.sizeBytes)
    : estimateBinaryDocumentTokens(attachment.sizeBytes);
}

export function estimateTextDocumentTokens(text: string): number {
  return Math.max(1, new TextEncoder().encode(text).byteLength);
}

function escapeXmlText(value: string): string {
  return value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
}

function escapeXmlAttribute(value: string): string {
  return escapeXmlText(value)
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;')
    .replaceAll('\t', '&#9;')
    .replaceAll('\n', '&#10;')
    .replaceAll('\r', '&#13;');
}

export function formatUntrustedTextDocument(params: {
  name: string;
  mimeType: string;
  text: string;
}): string {
  return `<untrusted_document name="${escapeXmlAttribute(params.name)}" mime_type="${escapeXmlAttribute(params.mimeType)}">\n${escapeXmlText(params.text)}\n</untrusted_document>`;
}

export function mergeRestoredDocumentAttachments(
  current: readonly DocumentAttachment[],
  restored: readonly DocumentAttachment[]
): { documents: DocumentAttachment[]; rejected: DocumentAttachment[] } {
  const documents = [...current];
  const rejected: DocumentAttachment[] = [];
  const seen = new Set(current.map(document => document.id));
  let totalBytes = current.reduce((total, document) => total + document.sizeBytes, 0);

  for (const document of restored) {
    if (seen.has(document.id)) continue;
    if (
      documents.length >= MAX_DOCUMENT_ATTACHMENTS ||
      totalBytes + document.sizeBytes > MAX_DOCUMENT_TOTAL_BYTES
    ) {
      rejected.push(document);
      continue;
    }
    seen.add(document.id);
    documents.push(document);
    totalBytes += document.sizeBytes;
  }

  return { documents, rejected };
}

export function canReuseDocumentAttachmentClaim(params: {
  sessionId: string;
  messageId: string;
  existingSessionId: unknown;
  existingMessageId: unknown;
  existingClaimIsReferenced: boolean;
}): boolean {
  return (
    params.existingSessionId === params.sessionId &&
    (params.existingMessageId === params.messageId || params.existingClaimIsReferenced)
  );
}
